import { useEffect, useCallback } from 'react'
import { addParticipants, selectSessionId } from 'src/app/session'
import { addErrorSDKToast, modifyParticipant } from 'src/utils/helpers'
import { OnParticipantChangeEvent } from 'src/types/sdk'
import { useAppDispatch, useAppSelector } from './useRTK'
import { Participants } from 'src/app/types'
import { appApi } from 'src/app/appApiQueries'
import { selectMeetingParticipants, updateParticipants } from 'src/app/meeting'

export const useOnParticipantChange = (showBadges: boolean) => {
  const dispatch = useAppDispatch()
  appApi.endpoints.getHostSettings.useQueryState('hostSettings')
  const storedMeetingParticipants = useAppSelector(selectMeetingParticipants)
  const activeSessionId = useAppSelector(selectSessionId)

  const fn = useCallback(
    ({ participants }: OnParticipantChangeEvent) => {
      const updatedMeetingParticipants = { ...storedMeetingParticipants } // must spread due to readonly
      const modifiedActiveSessionParticipants: Participants = {}
      const sendAppInvitationParticipantUUIDs: string[] = []
      // change the shape into an object with participantUUID as keys
      participants.forEach((participant) => {
        if (participant.status === 'join') {
          const modifiedParticipant = modifyParticipant(participant)
          // add to meeting participants
          updatedMeetingParticipants[participant.screenName] =
            modifiedParticipant
          if (activeSessionId) {
            sendAppInvitationParticipantUUIDs.push(participant.participantUUID)
            // add to active session participants
            modifiedActiveSessionParticipants[participant.screenName] =
              modifiedParticipant
          }
        } else {
          // remove from meeting participants
          delete updatedMeetingParticipants[participant.screenName]
        }
      })
      // we will be updating the meeting participants no matter what
      dispatch(updateParticipants(updatedMeetingParticipants))

      if (sendAppInvitationParticipantUUIDs.length > 0 && activeSessionId) {
        // we only care about adding participants to the active session, not removing them
        dispatch(addParticipants(modifiedActiveSessionParticipants))
        if (showBadges) {
          // send these new participants the app invite
          try {
            window.zoomSdk.sendAppInvitation({
              participantUUIDs: sendAppInvitationParticipantUUIDs,
            })
          } catch (e) {
            addErrorSDKToast(e)
          }
        }
      }
    },
    [activeSessionId, storedMeetingParticipants, dispatch, showBadges]
  )

  useEffect(() => {
    try {
      window.zoomSdk.addEventListener('onParticipantChange', fn)
    } catch {
      addErrorSDKToast('Error: cannot add onParticipantChange event')
    }

    return () => window.zoomSdk.removeEventListener('onParticipantChange', fn)
  }, [fn])
}
